/**
 * 日期工具函数集合
 */

/**
 * 格式化日期为 DD-MMM-YYYY 格式 (例如: 10-Jun-2025)
 * @param {Date|string} date - 日期对象或日期字符串
 * @returns {string} 格式化后的日期字符串 (DD-MMM-YYYY)
 */
export function formatDisplayDateFormat(date, day_format = true) {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return ""; // 无效日期返回空字符串

    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const day = String(d.getDate()).padStart(2, "0");
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();

    if (!day_format) {
        return `${month}-${year}`;
    }

    return `${day}-${month}-${year}`;
}

const MONTH_ABBREVS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

function canonicalMonthAbbrev(value) {
    if (!value) return "";
    return (
        MONTH_ABBREVS.find(
            (month) => month.toLowerCase() === String(value).toLowerCase(),
        ) || ""
    );
}

/**
 * Format a date as MMM YYYY (e.g. Aug 2026)
 * Also accepts API strings like "2026 Jun" or "Jun 2026".
 * @param {Date|string} date
 * @returns {string}
 */
export function formatMonthYear(date) {
    if (!date) return "";
    if (typeof date === "string") {
        const trimmed = date.trim();
        const yearThenMonth = trimmed.match(/^(\d{4})\s+([A-Za-z]{3})$/);
        if (yearThenMonth) {
            const month = canonicalMonthAbbrev(yearThenMonth[2]);
            return month ? `${month} ${yearThenMonth[1]}` : "";
        }
        const monthThenYear = trimmed.match(/^([A-Za-z]{3})\s+(\d{4})$/);
        if (monthThenYear) {
            const month = canonicalMonthAbbrev(monthThenYear[1]);
            return month ? `${month} ${monthThenYear[2]}` : "";
        }
    }
    const monthYear = formatDisplayDateFormat(date, false);
    return monthYear ? monthYear.replace("-", " ") : "";
}

/**
 * 格式化日期为 DD/MM/YYYY 格式
 * @param {Date|string} date - 日期对象或日期字符串
 * @returns {string} 格式化后的日期字符串 (DD/MM/YYYY)
 */
export function formatDateDDMMYYYY(date) {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
}

/**
 * 格式化日期时间为 YYYY-MM-DD HH:mm:ss 格式
 * @param {Date|string} date - 日期对象或日期字符串
 * @returns {string} 格式化后的日期时间字符串
 */
export function formatDateTime(date) {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * 格式化日期为本地化字符串
 * @param {Date|string} date - 日期对象或日期字符串
 * @param {string} locale - 地区代码，默认 'zh-CN'
 * @returns {string} 格式化后的日期字符串
 */
export function formatDateLocale(date, locale = "zh-CN") {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    return d.toLocaleDateString(locale);
}

/**
 * Format a date as YYYY-MM-DD (API / date-picker value).
 * @param {Date|string} date
 * @returns {string}
 */
export function toISODateString(date) {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Short month label for quick-range buttons (Jan, Feb, …).
 * @param {number} monthsAgo - 0 = current month, 1 = last month, etc.
 * @returns {string}
 */
export function getMonthShortLabel(monthsAgo = 0) {
    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
    return monthNames[target.getMonth()];
}

/**
 * Calendar month range as ISO strings.
 * @param {number} monthsAgo - 0 = current month, 1 = last month, etc.
 * @param {boolean} endAtToday - when true and monthsAgo is 0, end date is today
 * @returns {{ start: string, end: string }}
 */
export function getCalendarMonthRange(monthsAgo = 0, endAtToday = false) {
    const now = new Date();
    const anchor = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    let end;
    if (monthsAgo === 0 && endAtToday) {
        end = new Date(now);
    } else {
        end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    }
    return { start: toISODateString(start), end: toISODateString(end) };
}

/**
 * Rolling range covering the last N calendar months (inclusive), ending today or month-end.
 * @param {number} monthCount
 * @param {boolean} endAtToday
 * @returns {{ start: string, end: string }}
 */
export function getRollingMonthsRange(monthCount, endAtToday = true) {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - (monthCount - 1), 1);
    const end = endAtToday
        ? new Date(now)
        : new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: toISODateString(start), end: toISODateString(end) };
}

/**
 * Rolling range covering the last N days, ending today.
 * @param {number} dayCount
 * @returns {{ start: string, end: string }}
 */
export function getRollingDaysRange(dayCount) {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - dayCount);
    return { start: toISODateString(start), end: toISODateString(now) };
}

/**
 * Clamp an ISO date string to a minimum (inclusive).
 * @param {string} date
 * @param {string|null} minDate
 * @returns {string}
 */
export function clampISODateMin(date, minDate) {
    if (!date || !minDate) return date;
    return date < minDate ? minDate : date;
}

/**
 * Clamp an ISO date string to a maximum (inclusive).
 * @param {string} date
 * @param {string|null} maxDate
 * @returns {string}
 */
export function clampISODateMax(date, maxDate) {
    if (!date || !maxDate) return date;
    return date > maxDate ? maxDate : date;
}

/**
 * 获取日期范围（今天、本周、本月等）
 * @param {string} range - 范围类型: 'today', 'week', 'month', 'year'
 * @returns {Object} 包含 start 和 end 的日期对象
 */
export function getDateRange(range) {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);

    switch (range) {
        case "today":
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            break;
        case "week": {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1);
            start.setDate(diff);
            start.setHours(0, 0, 0, 0);
            end.setDate(start.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            break;
        }
        case "month":
            start.setDate(1);
            start.setHours(0, 0, 0, 0);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
            end.setHours(23, 59, 59, 999);
            break;
        case "year":
            start.setMonth(0, 1);
            start.setHours(0, 0, 0, 0);
            end.setMonth(11, 31);
            end.setHours(23, 59, 59, 999);
            break;
    }

    return { start, end };
}

/**
 * 计算两个日期之间的天数差
 * @param {Date|string} date1 - 第一个日期
 * @param {Date|string} date2 - 第二个日期
 * @returns {number} 天数差
 */
export function getDaysDifference(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

/**
 * 检查日期是否是今天
 * @param {Date|string} date - 要检查的日期
 * @returns {boolean}
 */
export function isToday(date) {
    const d = new Date(date);
    const today = new Date();
    return (
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear()
    );
}

/**
 * 添加天数到日期
 * @param {Date|string} date - 原始日期
 * @param {number} days - 要添加的天数
 * @returns {Date} 新日期
 */
export function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export function formatCurrency(value, decimalPlaces = 2) {
    if (value === null || value === undefined || isNaN(value)) {
        return "0.00";
    }
    return parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
}

/**
 * Abbreviated currency for tight UI (e.g. donut centers).
 * 20_000_000 → "20M", 2_700_763.1 → "2.7M", 15_000 → "15K".
 * Under 1_000 falls back to formatCurrency.
 */
export function formatCompactCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) {
        return "0.00";
    }
    const n = parseFloat(value);
    if (!Number.isFinite(n)) {
        return "0.00";
    }
    const abs = Math.abs(n);
    const sign = n < 0 ? "-" : "";

    const trim = (num) => {
        const fixed = num.toFixed(1);
        return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
    };

    if (abs >= 1e9) {
        return `${sign}${trim(abs / 1e9)}B`;
    }
    if (abs >= 1e6) {
        return `${sign}${trim(abs / 1e6)}M`;
    }
    if (abs >= 1e3) {
        return `${sign}${trim(abs / 1e3)}K`;
    }
    return formatCurrency(n);
}

export function mCubeSymbol() {
    return "m³";
}
export function almostEqualSymbol() {
    return "≈";
}

export function currencyPrefix(
    currency,
    font_size = 12,
    font_color = "text-muted-foreground",
) {
    return `<span class='${font_color} font-size-${font_size}'>${currency}&nbsp;</span>`;
}

/**
 * 默认导出所有函数
 */
export default {
    formatDisplayDateFormat,
    formatMonthYear,
    formatDateDDMMYYYY,
    formatDateTime,
    formatDateLocale,
    toISODateString,
    getMonthShortLabel,
    getCalendarMonthRange,
    getRollingMonthsRange,
    getRollingDaysRange,
    clampISODateMin,
    clampISODateMax,
    getDateRange,
    getDaysDifference,
    isToday,
    addDays,
    formatCurrency,
    formatCompactCurrency,
    mCubeSymbol,
    almostEqualSymbol,
    currencyPrefix,
};
